import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

import { defineCatalogue } from '../../i18n';
import { opsKeys, useGrantCredits } from '../api/queries';
import { reachOf, ReachFigure } from '../lib/units';
import { AmountField } from './AmountField';
import { FormDialog, fieldErrorOf, fieldControlProps } from '../../chrome/form';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/ui/field';
import { Input } from '@/ui/input';

const creditsCatalogue = defineCatalogue(
  {
    'grant.title': 'Conceder créditos',
    'note.label': 'Nota',
    'grant.submit': 'Conceder',
    'grant.amount.hint': 'Créditos adicionados ao saldo da organização.',
    'grant.note.hint': 'O motivo de adicionar estes créditos, para o registro permanente.',
    'priceEditor.link': 'Editar preço',
  },
  {
    'grant.title': 'Grant credits',
    'note.label': 'Note',
    'grant.submit': 'Grant',
    'grant.amount.hint': "Credits added to the organisation's balance.",
    'grant.note.hint': 'The reason you are adding these credits, for the permanent ledger.',
    'priceEditor.link': 'Edit price',
  },
);
const t = creditsCatalogue.t;

/**
 * The API has two verbs -- `grant_credits` (positive-only) and
 * `adjust_credits` (signed) -- so "grant -500" is not something anybody
 * types by accident. Two dialogs, not one signed form, honours that split
 * on the surface an operator actually uses.
 */
export function GrantDialog(props: {
  tenantId: string;
  tenantName: string;
  availableCredits: number;
  tenantCreditsPerDay: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const grant = useGrantCredits();
  const queryClient = useQueryClient();
  const rawFailure = grant.error?.failure ?? null;
  const noteError = fieldErrorOf(rawFailure, 'note');
  const amountError = fieldErrorOf(rawFailure, 'credits');

  const canSubmit = amount !== null && amount > 0 && note.trim() !== '' && !grant.isPending;
  // §18.5: what the amount buys, recomputed on every keystroke. Floored and
  // marked as an upper bound by `reachOf`/`ReachFigure` themselves -- this
  // dialog only supplies the amount-in-progress as part of the balance.
  const reach = reachOf(props.availableCredits + (amount ?? 0), props.tenantCreditsPerDay);

  function submit(): void | Promise<unknown> {
    if (!canSubmit || amount === null) return;
    return (
      grant
        .mutateAsync({ tenantId: props.tenantId, credits: amount, note: note.trim() })
        // `useGrantCredits` itself invalidates nothing -- this dialog is its
        // only caller, so which queries a grant should refresh has one
        // answer and belongs here, not hardcoded into a mutation hook a
        // second caller might need a different answer from.
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: opsKeys.tenant(props.tenantId) });
          void queryClient.invalidateQueries({ queryKey: opsKeys.movements(props.tenantId) });
          setAmount(null);
          setNote('');
        })
    );
  }

  return (
    <FormDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={t('grant.title')}
      description={props.tenantName}
      submitLabel={t('grant.submit')}
      pending={grant.isPending}
      failure={grant.error}
      // FormDialog's own submit button only gates on `pending`; an invalid
      // amount or a missing note is refused here instead of by disabling it.
      onSubmit={(event) => {
        event.preventDefault();
        return submit();
      }}
    >
      <AmountField
        sign={null}
        value={amount}
        onChange={setAmount}
        hint={t('grant.amount.hint')}
        error={amountError}
      />
      <Field data-invalid={noteError !== null || undefined}>
        <FieldLabel htmlFor="grant-note">{t('note.label')}</FieldLabel>
        <Input
          id="grant-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          {...fieldControlProps(rawFailure, 'note', 'grant-note-error')}
        />
        <FieldDescription>{t('grant.note.hint')}</FieldDescription>
        <FieldError id="grant-note-error">{noteError}</FieldError>
      </Field>
      {/* No balance-after preview: that would be one subtraction the
       *  client computed instead of the server. The reach below is a
       *  duration over figures the server already sent, which has no
       *  server-sent counterpart to disagree with. */}
      <div data-testid="grant-reach" data-bound={reach.kind === 'days' ? 'upper' : undefined}>
        <ReachFigure reach={reach} />
        {reach.kind === 'unpriced' && <Link to="/prices">{t('priceEditor.link')}</Link>}
      </div>
    </FormDialog>
  );
}
